/** @jsx jsx */
import React from 'react';

import { jsx } from '@emotion/react';
import { Button, Flex, Text } from '@opendoor/bricks/core';
import Input, { InputProps } from '@opendoor/bricks/core/Input';
import { checkTelValidity } from '@opendoor/bricks/core/Input/helpers';
import Link from '@opendoor/bricks/core/Link';
import { isNil } from 'lodash';

import AccountFormControls from './AccountFormControls';
import { Disclaimer, DisclaimerProps, Form, PHONE_DEFAULT_DISCLAIMER } from './shared';

const INVALID_EMAIL_ERROR = 'Please enter a valid email.';

export interface IAccountCombinedFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email?: string;
  name?: string;
  phone?: string;
  loading?: boolean;
  onRegister: (email: string, password: string, name: string, phone: string) => void;
  customEmailValidityCheck?: (email: string) => Promise<{ suggestion: string; valid: boolean }>;
  customEmailValidityCheckDelay?: number;
  phoneNumberInputOptional?: boolean;
  phoneNumberInputHidden?: boolean;
  disclaimerProps?: DisclaimerProps;
  handleSignInFromComboForm?: () => void;
  fullNamePlaceholder?: string;
  fullNameLabel?: string;
  EmailInput: React.FC<{
    analyticsName: string;
    required: boolean;
    unboundedWidth: boolean;
    type: 'email';
    id: 'email-login-input';
    name: 'email';
    minLength: number;
    value?: string;
    errorMessage: string;
    onChange: InputProps['onChange'];
    placeholder: string;
    label: string;
    width: string;
    fontSize: string;
    delay: number;
    customEmailValidityCheck?: (email: string) => Promise<{ suggestion: string; valid: boolean }>;
    autoComplete: string;
    mb?: number;
  }>;
  NameInput: React.FC<{
    analyticsName: string;
    required: boolean;
    unboundedWidth: boolean;
    type: 'text';
    id: string;
    name: 'name';
    className: 'od-register-name';
    onChange: InputProps['onChange'];
    placeholder: string;
    label: string;
    delay?: number;
    errorMessage?: string;
    fontSize?: string;
    value?: string;
    autoComplete: string;
    autoFocus?: boolean;
    mb?: number;
  }>;
  PhoneInput: React.FC<{
    analyticsName: string;
    type: 'tel';
    id: 'phone';
    className: 'od-register-phone';
    label: string;
    name: 'phone';
    onChange: InputProps['onChange'];
    required: boolean;
    unboundedWidth: boolean;
    value?: string;
    fontSize: string;
    delay: number;
    autoComplete: string;
  }>;
}

export interface IAccountCombinedFormState {
  hasEmailError: boolean;
  hasPhoneError: boolean;
  hasNameError: boolean;
  email: string;
  name: string;
  phone: string;
  password: string;
}

export default class AccountCombinedForm extends React.Component<
  IAccountCombinedFormProps,
  IAccountCombinedFormState
> {
  constructor(props: IAccountCombinedFormProps) {
    super(props);
    this.state = {
      hasEmailError: false,
      email: props.email,
      name: props.name,
      password: '',
      hasNameError: false,
      phone: props.phone,
      // If the phone number is required, then we have a phone error by default
      hasPhoneError: props.phoneNumberInputOptional ? false : true,
    };
  }
  public static defaultProps = {
    email: '',
    phoneNumberInputOptional: true,
    phoneNumberInputHidden: false,
    EmailInput: Input,
    NameInput: Input,
    PhoneInput: Input,
  };

  public componentDidMount() {
    this.maybePrefillAccount();
  }

  public validatePasswordLength = (password: string) => {
    return password.length >= 5 && password.length <= 128;
  };

  public validateName = (name: string) => {
    return name.trim() !== '';
  };

  public onNameChange: InputProps['onChange'] = (_event, name) => {
    this.setState({
      hasNameError: isNil(name) || !this.validateName(name),
      name,
    });
  };

  public getPhoneState = (phone?: string) => {
    if (isNil(phone) || phone === '') {
      return { hasPhoneError: !this.props.phoneNumberInputOptional, phone };
    }

    return {
      hasPhoneError: !checkTelValidity(phone),
      phone,
    };
  };

  public onPhoneChange: InputProps['onChange'] = (_event, phone) => {
    this.setState(this.getPhoneState(phone));
  };

  public onEmailChange: InputProps['onChange'] = (_event, value, validFormat) => {
    const email = isNil(value) ? '' : value.trim();
    this.setState({ email, hasEmailError: isNil(value) || !validFormat });
  };

  public handleSubmit = (e: Partial<React.FormEvent<HTMLFormElement>>) => {
    e.preventDefault();
    const { email, password, hasEmailError, hasPhoneError, phone, name, hasNameError } = this.state;
    const { onRegister } = this.props;

    if (hasEmailError || hasNameError || hasPhoneError) {
      return;
    }

    onRegister(email, password, name, phone);
  };

  public render() {
    const {
      analyticsName,
      loading,
      phoneNumberInputOptional,
      phoneNumberInputHidden,
      customEmailValidityCheck,
      customEmailValidityCheckDelay,
      handleSignInFromComboForm,
      fullNamePlaceholder,
      fullNameLabel,
      disclaimerProps,
      EmailInput,
      NameInput,
      PhoneInput,
    } = this.props;
    const { email, hasEmailError, name, hasNameError, phone, hasPhoneError } = this.state;

    const shouldDisableSubmit =
      hasEmailError || !this.state.email || !this.state.name || hasNameError || hasPhoneError;

    const phoneLabelText = phoneNumberInputOptional ? 'Phone (optional)' : 'Phone';

    const actionText = 'Continue';

    const disclaimerText =
      disclaimerProps?.formatText?.({ actionText }) || PHONE_DEFAULT_DISCLAIMER(actionText);

    return (
      <>
        <Form onSubmitCapture={this.handleSubmit}>
          <EmailInput
            analyticsName={`${analyticsName}-combined-email`}
            required
            unboundedWidth
            type="email"
            id="email-login-input"
            name="email"
            minLength={1}
            value={email}
            errorMessage={INVALID_EMAIL_ERROR}
            onChange={this.onEmailChange}
            placeholder={'you@example.com'}
            label="Email"
            width="100%"
            fontSize="s1"
            delay={customEmailValidityCheckDelay ?? 0}
            customEmailValidityCheck={customEmailValidityCheck}
            autoComplete="email"
            mb={4}
          />
          <NameInput
            analyticsName={`${analyticsName}-combined-name`}
            required
            unboundedWidth
            type="text"
            id="name-login-input"
            name="name"
            className="od-register-name"
            onChange={this.onNameChange}
            placeholder={fullNamePlaceholder ? fullNamePlaceholder : 'Jasper Smith'}
            label={fullNameLabel ? fullNameLabel : 'Full name'}
            value={name}
            autoComplete="name"
            mb={4}
          />
          {!phoneNumberInputHidden && (
            <PhoneInput
              analyticsName={`${analyticsName}-combined-phone`}
              type="tel"
              id="phone"
              className="od-register-phone"
              label={phoneLabelText}
              name="phone"
              onChange={this.onPhoneChange}
              required={!phoneNumberInputOptional}
              unboundedWidth={true}
              value={phone}
              fontSize="s1"
              delay={0}
              autoComplete="tel"
            />
          )}
          {disclaimerProps?.location === 'top' && !phoneNumberInputHidden && (
            <Disclaimer my={5}>{disclaimerText}</Disclaimer>
          )}
          <AccountFormControls
            actionText={actionText}
            analyticsName={`${analyticsName}-combined`}
            loading={loading}
            disabled={shouldDisableSubmit}
            submitA11yProps={{ 'aria-label': '' }}
            showForgotPassword={false}
          />
          <Flex mt={[4, 4, 7]} mb={6}>
            <Text fontSize="s0" mr={2}>
              Already have an account?{' '}
            </Text>
            <Button
              onClick={() => {
                handleSignInFromComboForm();
              }}
              variant="link"
              aria-label="Go to account login"
              analyticsName="reception-fe-already-have-account"
              fontSize="s0"
            >
              Sign in
            </Button>
          </Flex>
        </Form>
        <Disclaimer mt={5}>
          Your information is protected by our&nbsp;
          <Link
            href="https://www.opendoor.com/w/privacy"
            display="inline"
            target="_blank"
            analyticsName={`${analyticsName}-privacy-policy`}
            aria-label="Opens a new page with Opendoor's privacy policy"
          >
            Privacy Policy
          </Link>
          .<br></br>
          <br></br>
          {!phoneNumberInputHidden && disclaimerProps?.location !== 'top' && disclaimerText}
        </Disclaimer>
      </>
    );
  }

  private maybePrefillAccount = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.searchParams);
    const phoneNumber = params.get('phoneNumber') || this.props.phone;
    const { phone, hasPhoneError } = this.getPhoneState(phoneNumber);

    this.setState({
      name: params.get('name') || '',
      phone: hasPhoneError ? '' : phone,
      hasPhoneError,
    });
  };
}
